/**
 * ChatManager - The Business Logic Layer of the Chat System
 * 
 * ⚠️ CRITICAL ARCHITECTURE COMPONENT - READ BEFORE MODIFYING
 * 
 * This hook is the SINGLE BUSINESS LOGIC LAYER for all chat operations.
 * It implements a fully localStorage-reactive architecture with zero React state.
 * 
 * KEY PRINCIPLES:
 * 1. ALL state comes from localStorage (never React state)
 * 2. Components automatically re-render when localStorage changes
 * 3. Automatic message loading/caching for instant performance
 * 4. Thread switching triggers automatic message reload
 * 5. Infinite scroll support for large conversations
 * 
 * RULES FOR MODIFICATION:
 * ❌ NEVER add React state for messages, threadId, or chat status
 * ❌ NEVER create competing state systems
 * ❌ NEVER bypass localStorage for chat data
 * ✅ ALWAYS use localStorage utilities for state management
 * ✅ ALWAYS emit custom events for state changes
 * ✅ ALWAYS maintain single source of truth
 * 
 * See: docs/CHAT_SYSTEM_ARCHITECTURE.md for complete guidelines
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { CopilotMessage } from './types/database-types';
import { MessageDisplay } from './types/chat-hook-types';
import { useMessageLoading } from './hooks/useMessageLoading';
import { usePaginatedMessages } from './hooks/usePaginatedMessages';
import { useChatInitialization } from './hooks/useChatInitialization';
import { logInfo, logError, LogCategory } from '@/utils/logger';
import { 
  saveCopilotSidebarState, 
  getCopilotSidebarState,
  getMessageCache,
  setMessageCache,
  getChatState,
  setChatState,
  clearMessageCache,
  isMessageCacheValid
} from '@/utils/localStorage';
import { transformCopilotMessagesToDisplay } from './utils/messageTransformers';

export type ChatManagerState = 'idle' | 'loading' | 'ready' | 'error';

export interface ChatManagerReturn {
  // State
  activeThreadId: string | null;
  messages: MessageDisplay[];
  state: ChatManagerState;
  isGenerating: boolean;
  error: string | null;
  
  // Operations
  loadThread: (threadId?: string) => Promise<string | null>;
  setMessages: (messages: MessageDisplay[] | ((prev: MessageDisplay[]) => MessageDisplay[])) => void;
  setIsGenerating: (generating: boolean) => void;
  setError: (error: string | null) => void;
  clearChat: () => void;
  resetError: () => void;
  isThreadLoaded: (threadId: string) => boolean;
  isChatContextReady: () => boolean;
  
  // Infinite Scroll Support
  hasOlderMessages: boolean;
  isLoadingOlder: boolean;
  loadOlderMessages: () => Promise<void>;
  totalMessageCount: number;
  loadedMessageCount: number;
}

/**
 * useChatManager - Fully localStorage-reactive business logic layer with infinite scroll
 * 
 * COMPLETE REFACTOR: Now 100% localStorage-based with zero React state + infinite scroll
 * 
 * Key principles:
 * - Single source of truth (localStorage for ALL state)
 * - Instant loading via message cache
 * - Automatic cache invalidation and refresh
 * - Reactive to localStorage changes across all components
 * - Automatic message reloading on thread changes
 * - Infinite scroll for large conversations
 * - Zero React state - pure localStorage consumer
 */
export const useChatManager = (user: any, currentAssistant?: any): ChatManagerReturn => {
  // ✅ REACTIVE SYSTEM: Force re-render mechanism for localStorage changes
  // This is the ONLY React state - used purely for triggering re-renders
  const [, forceUpdate] = useState({});
  const triggerUpdate = useCallback(() => {
    forceUpdate({});
  }, []);

  // Operation tracking to prevent duplicates
  const operationsRef = useRef<Set<string>>(new Set());

  // ✅ REACTIVE: Always read current thread ID from localStorage
  // NEVER store this in React state - always read fresh from localStorage
  const getCurrentThreadId = useCallback((): string | null => {
    const savedState = getCopilotSidebarState();
    return savedState?.currentThreadId || null;
  }, []);

  // ✅ REACTIVE: Get activeThreadId dynamically from localStorage
  // This ensures components always have the current thread ID
  const activeThreadId = getCurrentThreadId();

  // ✅ REACTIVE: Get messages from localStorage cache
  // NEVER store messages in React state - always read from cache
  const messages = activeThreadId ? getMessageCache(activeThreadId) : [];

  // ✅ REACTIVE: Get chat state from localStorage
  // All loading states, errors, and generation flags come from localStorage
  const currentChatState = activeThreadId ? getChatState(activeThreadId) : {
    state: 'idle' as const,
    isGenerating: false,
    error: null,
    lastActivity: Date.now()
  };

  // ✅ INFINITE SCROLL: Use paginated messages for large conversations
  const {
    messages: paginatedMessages,
    hasNextPage: hasOlderMessages,
    fetchNextPage: fetchOlderMessages,
    isFetchingNextPage: isLoadingOlder,
    totalCount: totalMessageCount,
    loadedCount: loadedMessageCount,
    isLoading: isPaginationLoading
  } = usePaginatedMessages({
    threadId: activeThreadId || '',
    pageSize: 20,
    enabled: !!activeThreadId && messages.length >= 20 // Only use pagination for large conversations
  });

  // ✅ STABLE HOOKS: Initialize hooks at top level
  // These hooks provide the core functionality for message operations
  const { loadMessages } = useMessageLoading(
    (newMessages) => {
      if (activeThreadId) {
        // ✅ CACHE UPDATE: Store messages in localStorage cache
        setMessageCache(activeThreadId, newMessages, false);
        // Event firing handled by setMessageCache
      }
    },
    (loading) => {
      if (activeThreadId) {
        // ✅ STATE UPDATE: Update loading state in localStorage
        const currentState = getChatState(activeThreadId);
        setChatState(activeThreadId, {
          ...currentState,
          state: loading ? 'loading' : 'ready'
        });
      }
    },
    (error) => {
      if (activeThreadId) {
        // ✅ ERROR HANDLING: Store errors in localStorage
        const currentState = getChatState(activeThreadId);
        setChatState(activeThreadId, {
          ...currentState,
          error,
          state: 'error'
        });
      }
    }
  );

  const { initializeChat } = useChatInitialization(
    activeThreadId,
    (newThreadId) => {
      // ✅ THREAD MANAGEMENT: Update localStorage currentThreadId
      const currentState = getCopilotSidebarState();
      saveCopilotSidebarState({
        ...currentState,
        currentThreadId: newThreadId || undefined
      });

      // ✅ REACTIVE SYSTEM: Emit change event for synchronization
      window.dispatchEvent(new CustomEvent('localStorage-change', {
        detail: { key: 'currentThreadId', value: newThreadId }
      }));
    },
    (newMessages) => {
      if (activeThreadId) {
        // ✅ INITIALIZATION: Cache messages during thread setup
        setMessageCache(activeThreadId, newMessages, false);
      }
    },
    async (threadIdToLoad) => {
      // ✅ MESSAGE LOADING: Use the message loading hook
      await loadMessages(threadIdToLoad);
    },
    user,
    currentAssistant
  );

  // ✅ INFINITE SCROLL: Merge cached and paginated messages for large conversations
  const effectiveMessages = useMemo(() => {
    // For conversations with 20+ messages, use paginated messages for better performance
    if (messages.length >= 20 && paginatedMessages.length > 0) {
      // Transform paginated messages to display format
      const transformedPaginated = transformCopilotMessagesToDisplay(paginatedMessages);
      
      // Merge with cached messages, prioritizing newer cached messages
      const mergedMessages = [...transformedPaginated];
      
      // Add any newer cached messages that aren't in paginated results
      const latestPaginatedTime = paginatedMessages.length > 0 
        ? new Date(paginatedMessages[paginatedMessages.length - 1].createdAt).getTime()
        : 0;
        
      const newerCachedMessages = messages.filter(msg => 
        new Date(msg.createdAt).getTime() > latestPaginatedTime
      );
      
      return [...mergedMessages, ...newerCachedMessages];
    }
    
    // For smaller conversations, use cached messages for better performance
    return messages;
  }, [messages, paginatedMessages]);

  // ✅ INFINITE SCROLL: Load older messages function
  const loadOlderMessages = useCallback(async () => {
    if (!hasOlderMessages || isLoadingOlder) return;
    
    try {
      logInfo('ChatManager: Loading older messages', {
        category: LogCategory.STATE,
        data: { 
          threadId: activeThreadId,
          currentCount: loadedMessageCount,
          totalCount: totalMessageCount
        }
      });
      
      await fetchOlderMessages();
      
      logInfo('ChatManager: Older messages loaded successfully', {
        category: LogCategory.STATE,
        data: { 
          threadId: activeThreadId,
          newCount: loadedMessageCount
        }
      });
    } catch (error: any) {
      logError('ChatManager: Failed to load older messages', {
        category: LogCategory.STATE,
        data: { error: error.message, threadId: activeThreadId }
      });
    }
  }, [hasOlderMessages, isLoadingOlder, fetchOlderMessages, activeThreadId, loadedMessageCount, totalMessageCount]);

  // ✅ REACTIVE: Subscribe to localStorage changes via custom event
  // This ensures ALL instances of ChatManager stay synchronized
  useEffect(() => {
    const handleStorageChange = (event: CustomEvent) => {
      // ✅ SYNCHRONIZATION: Trigger update for any localStorage change
      // This keeps all components in sync across the entire app
      triggerUpdate();
    };

    // Listen for custom localStorage change events
    window.addEventListener('localStorage-change', handleStorageChange as EventListener);

    return () => {
      window.removeEventListener('localStorage-change', handleStorageChange as EventListener);
    };
  }, [triggerUpdate]);

  // ✅ PERFORMANCE: Auto-reload messages when thread changes
  // This provides instant loading for cached messages, database loading for new threads
  useEffect(() => {
    if (!activeThreadId) return;

    const loadMessagesForThread = async () => {
      // ✅ CACHE CHECK: Use cached messages if valid to avoid database calls
      if (isMessageCacheValid(activeThreadId)) {
        logInfo('ChatManager: Using cached messages', {
          category: LogCategory.STATE,
          data: { threadId: activeThreadId, messageCount: messages.length }
        });
        return;
      }

      // Cache invalid or doesn't exist - load from database
      const operationKey = `auto-load-messages-${activeThreadId}`;
      
      // ✅ DEDUPLICATION: Prevent duplicate loading operations
      if (operationsRef.current.has(operationKey)) {
        return; // Already loading
      }

      operationsRef.current.add(operationKey);

      try {
        logInfo('ChatManager: Auto-loading messages for thread', {
          category: LogCategory.STATE,
          data: { threadId: activeThreadId }
        });

        // ✅ LOADING STATE: Set loading state in localStorage
        setChatState(activeThreadId, {
          state: 'loading',
          isGenerating: false,
          error: null
        });

        // ✅ DATABASE LOAD: Use the message loading hook
        await loadMessages(activeThreadId);

        // ✅ SUCCESS STATE: Set ready state in localStorage
        setChatState(activeThreadId, {
          state: 'ready',
          isGenerating: false,
          error: null
        });

        logInfo('ChatManager: Auto-loaded messages successfully', {
          category: LogCategory.STATE,
          data: { threadId: activeThreadId }
        });

      } catch (error: any) {
        logError('ChatManager: Failed to auto-load messages', {
          category: LogCategory.STATE,
          data: { error: error.message, threadId: activeThreadId }
        });

        // ✅ ERROR STATE: Store error in localStorage
        setChatState(activeThreadId, {
          state: 'error',
          isGenerating: false,
          error: error.message
        });
      } finally {
        // ✅ CLEANUP: Always clean up operation tracking
        operationsRef.current.delete(operationKey);
      }
    };

    loadMessagesForThread();
  }, [activeThreadId, loadMessages]); // Include loadMessages in deps

  // ✅ DEDUPLICATION: Helper to prevent duplicate operations
  const executeWithDeduplication = useCallback(async <T>(
    operationKey: string,
    operation: () => Promise<T>
  ): Promise<T | null> => {
    if (operationsRef.current.has(operationKey)) {
      logInfo('ChatManager: Operation already in progress, skipping', {
        category: LogCategory.STATE,
        data: { operationKey }
      });
      return null;
    }

    operationsRef.current.add(operationKey);
    try {
      return await operation();
    } finally {
      operationsRef.current.delete(operationKey);
    }
  }, []);

  // ✅ CACHE OPERATIONS: Message operations that update localStorage cache
  // These provide the interface for components to update messages

  /**
   * Update messages in localStorage cache
   * ⚠️ USAGE: For adding new messages or updating existing ones
   * This triggers re-renders across ALL components using ChatManager
   */
  const setMessages = useCallback((newMessages: MessageDisplay[] | ((prev: MessageDisplay[]) => MessageDisplay[])) => {
    if (!activeThreadId) return;

    const updatedMessages = typeof newMessages === 'function' 
      ? newMessages(getMessageCache(activeThreadId))
      : newMessages;

    // ✅ CACHE UPDATE: Update cache and trigger events
    setMessageCache(activeThreadId, updatedMessages, false);
    // Event firing handled by setMessageCache
  }, [activeThreadId]);

  /**
   * Update generation state in localStorage
   * ⚠️ USAGE: For showing/hiding typing indicators and loading states
   * This triggers re-renders across ALL components using ChatManager
   */
  const setIsGenerating = useCallback((generating: boolean) => {
    if (!activeThreadId) return;

    const currentState = getChatState(activeThreadId);
    setChatState(activeThreadId, {
      ...currentState,
      isGenerating: generating
    });
    // Event firing handled by setChatState
  }, [activeThreadId]);

  /**
   * Set error state in localStorage
   * ⚠️ USAGE: For displaying error messages to users
   * This triggers re-renders across ALL components using ChatManager
   */
  const setError = useCallback((error: string | null) => {
    if (!activeThreadId) return;

    const currentState = getChatState(activeThreadId);
    setChatState(activeThreadId, {
      ...currentState,
      error,
      state: error ? 'error' : 'ready'
    });
    // Event firing handled by setChatState
  }, [activeThreadId]);

  // ✅ PUBLIC API: Core operations exposed to components

  /**
   * Load a specific thread (or create new one if no threadId provided)
   * ⚠️ USAGE: Primary method for thread switching and initialization
   * Updates localStorage and triggers automatic message loading
   */
  const loadThread = useCallback(async (threadId?: string): Promise<string | null> => {
    const operationKey = `load-thread-${threadId || 'new'}`;
    
    return executeWithDeduplication(operationKey, async () => {
      logInfo('ChatManager: Loading thread', {
        category: LogCategory.STATE,
        data: { threadId: threadId || 'new', currentActive: activeThreadId }
      });

      try {
        // ✅ INITIALIZATION: Use the initialization hook for thread setup
        const loadedThreadId = await initializeChat(threadId);
        
        if (loadedThreadId) {
          // ✅ SUCCESS: Set ready state for the loaded thread
          setChatState(loadedThreadId, {
            state: 'ready',
            isGenerating: false,
            error: null
          });
          
          logInfo('ChatManager: Thread loaded successfully', {
            category: LogCategory.STATE,
            data: { threadId: loadedThreadId }
          });
          
          return loadedThreadId;
        } else {
          logError('ChatManager: Failed to load thread', {
            category: LogCategory.STATE,
            data: { threadId }
          });
          return null;
        }
      } catch (error: any) {
        logError('ChatManager: Failed to load thread', {
          category: LogCategory.STATE,
          data: { error: error.message, threadId }
        });
        
        if (threadId) {
          // ✅ ERROR HANDLING: Store error state for the thread
          setChatState(threadId, {
            state: 'error',
            isGenerating: false,
            error: error.message
          });
        }
        return null;
      }
    });
  }, [executeWithDeduplication, initializeChat]);

  /**
   * Clear all chat state and return to initial state
   * ⚠️ USAGE: For logout, reset, or starting fresh
   * Clears ALL cached data and resets to initial state
   */
  const clearChat = useCallback(() => {
    logInfo('ChatManager: Clearing chat', { category: LogCategory.STATE });
    
    // ✅ RESET: Clear current thread from localStorage
    const currentState = getCopilotSidebarState();
    saveCopilotSidebarState({
      ...currentState,
      currentThreadId: undefined
    });

    // ✅ CLEANUP: Clear all message cache and chat state
    clearMessageCache();
    // Event firing handled by clearMessageCache
  }, []);

  /**
   * Reset error state for current thread
   * ⚠️ USAGE: For clearing error messages after user acknowledges them
   */
  const resetError = useCallback(() => {
    if (!activeThreadId) return;

    const currentState = getChatState(activeThreadId);
    setChatState(activeThreadId, {
      ...currentState,
      error: null,
      state: 'ready'
    });
    // Event firing handled by setChatState
  }, [activeThreadId]);

  /**
   * Check if a specific thread is loaded and ready
   * ⚠️ USAGE: For conditional UI rendering based on thread state
   */
  const isThreadLoaded = useCallback((threadId: string) => {
    const currentActiveThreadId = getCurrentThreadId();
    const chatState = getChatState(threadId);
    return currentActiveThreadId === threadId && chatState.state === 'ready';
  }, [getCurrentThreadId]);

  /**
   * Check if the chat context is fully ready to accept messages
   * ⚠️ USAGE: For validating that messages can be sent safely
   */
  const isChatContextReady = useCallback(() => {
    return (
      !!activeThreadId &&
      currentChatState.state === 'ready' &&
      !currentChatState.isGenerating &&
      !currentChatState.error
    );
  }, [activeThreadId, currentChatState]);

  // ✅ READY STATE: Emit chat context ready event when context becomes ready
  useEffect(() => {
    if (isChatContextReady()) {
      // Small delay to ensure all components are fully mounted
      const readyTimer = setTimeout(() => {
        window.dispatchEvent(new CustomEvent('chat-context-ready', {
          detail: { 
            threadId: activeThreadId,
            timestamp: Date.now()
          }
        }));
      }, 50);
      
      return () => clearTimeout(readyTimer);
    }
  }, [activeThreadId, currentChatState.state, currentChatState.isGenerating, currentChatState.error]);

  // ✅ RETURN: Public API with infinite scroll support
  return {
    // State
    activeThreadId,
    messages: effectiveMessages,
    state: currentChatState.state,
    isGenerating: currentChatState.isGenerating,
    error: currentChatState.error,
    
    // Operations
    loadThread,
    setMessages,
    setIsGenerating,
    setError,
    clearChat,
    resetError,
    isThreadLoaded,
    isChatContextReady,
    
    // Infinite Scroll Support
    hasOlderMessages: hasOlderMessages && effectiveMessages.length >= 20,
    isLoadingOlder: isLoadingOlder || isPaginationLoading,
    loadOlderMessages,
    totalMessageCount,
    loadedMessageCount: effectiveMessages.length
  };
}; 